import { PortableTextProps } from "@portabletext/react";
import cx from "classnames";
import Image from "@/components/Image";
import NewsletterWidgetBottom from "@/components/newsletter/NewsletterWidgetBottom";
import PortableText from "@/components/PortableText";
import ShareBar from "@/components/social/ShareBar";
import ShareButtons from "@/components/social/ShareButtons";
import Tag from "@/components/tag/Tag";
import { FALLBACK_IMAGE } from "@/constants/media";
import { Article } from "@/graphql/gql/graphql";
import { ArticleContextProvider } from "@/utils/hooks/useArticleContext";
import isClient from "@/utils/isClient";
import HeroVideo from "./HeroVideo";
import styles from "./articleComponent.module.scss";

export type ArticleComponentProps = {
  article: Article & { parsedBody: PortableTextProps["value"] };
  notify: () => void;
  loading?: boolean;
};

const ArticleComponent = ({
  article,
  notify,
  loading = false,
}: ArticleComponentProps) => {
  const {
    title,
    firstPublishedAt,
    author,
    updatedBy,
    hero,
    sport: { name: sportName, slug: sportSlug },
    hideTopNewsletter,
    hideBottomNewsletter,
    parsedBody,
  } = article;

  const href = isClient() ? window.location.href : "";

  return (
    <ArticleContextProvider
      author={author.name}
      sport={sportName}
      sportSlug={sportSlug}
      title={title}
    >
      <article className={cx(loading && styles.loading)} aria-busy={loading}>
        <Tag intent="neutral" label={sportName} href={`/${sportSlug}`} />
        <h1 className={styles.articleHeading}>{title}</h1>
        <ShareBar
          author={author}
          date={firstPublishedAt ?? ""}
          updatedBy={updatedBy}
          rightSlot={
            <ShareButtons
              dispatchFunction={notify}
              url={href}
              articleTitle={title}
            />
          }
          hideTopNewsletter={hideTopNewsletter}
        />
        <div className={styles.imgOrVideo}>
          {hero?.jwPlayerId ? (
            <HeroVideo title={title} hero={hero} />
          ) : (
            <Image
              priority
              width={964}
              height={477}
              alt={title}
              src={hero?.imageUrl?.url ?? FALLBACK_IMAGE}
              style={{ width: "100%", height: "auto" }}
              sizes="(max-width: 960px) 50vw, 100vw"
            />
          )}
        </div>
        <section className={styles.portableText}>
          <PortableText value={parsedBody} />
        </section>
        {!hideBottomNewsletter && <NewsletterWidgetBottom />}
        <div className={styles.shareContainer}>
          <ShareButtons
            dispatchFunction={notify}
            url={href}
            articleTitle={title}
          />
        </div>
      </article>
    </ArticleContextProvider>
  );
};

export default ArticleComponent;
